import { useCallback, useMemo } from 'react';
import type { HTMLAttributes, JSX, Key, SyntheticEvent } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type {
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';

import { EMPTY_STRING } from '@e-socna/empty';

import { useIdOptionsResolver } from './hooks/useIdOptionsResolver.hook';
import type { IdOption } from './types';

interface FilterOptionsState {
  readonly inputValue: string;
}

interface Props<TId extends Key, TOption extends IdOption<TId>> {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly filterOptions?: (
    options: readonly TOption[],
    state: Readonly<FilterOptionsState>,
  ) => readonly TOption[];
  readonly helperText?: string | null;
  readonly label?: string | null;
  readonly loading?: boolean;
  readonly loadingText?: string;
  readonly name?: string | null;
  readonly noOptionsText?: string;
  readonly onBlur?: (() => void) | null;
  readonly onChange: (id: TId | typeof EMPTY_STRING) => void;
  readonly onClose?: () => void;
  readonly onOpen?: () => void;
  readonly open?: boolean;
  readonly options: readonly TOption[];
  readonly required?: boolean;
  readonly renderOption?: (
    props: HTMLAttributes<HTMLLIElement> & Readonly<{ key: Key }>,
    option: TOption,
    state: AutocompleteRenderOptionState,
  ) => JSX.Element;
  readonly value: TId | typeof EMPTY_STRING;
}

export const IdAutocomplete = <TId extends Key, TOption extends IdOption<TId>>({
  disabled = false,
  error = false,
  filterOptions,
  helperText = null,
  label = null,
  loading = false,
  loadingText,
  name = null,
  noOptionsText,
  onBlur = null,
  onChange,
  onClose,
  onOpen,
  open,
  options,
  required = false,
  renderOption,
  value,
}: Readonly<Props<TId, TOption>>) => {
  const { getOptionDisabled, getOptionGroup, getOptionLabel, ids, optionById } =
    useIdOptionsResolver<TId, TOption>(options);

  const handleChange = useCallback(
    (_event: Readonly<SyntheticEvent>, id: TId | null) => {
      onChange(id ?? EMPTY_STRING);
    },
    [onChange],
  );

  const autocompleteValue = value === EMPTY_STRING ? null : value;

  const renderInput = useCallback(
    (params: Readonly<AutocompleteRenderInputParams>) => (
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        disabled={disabled}
        error={error}
        helperText={helperText}
        label={label}
        name={name ?? undefined}
        onBlur={onBlur ?? undefined}
        required={required}
      />
    ),
    [disabled, error, helperText, label, name, onBlur, required],
  );

  const resolvedRenderOption = useCallback(
    (
      itemProps: HTMLAttributes<HTMLLIElement> & Readonly<{ key: Key }>,
      id: TId,
      state: AutocompleteRenderOptionState,
    ) => {
      const option = optionById.get(id);

      if (!option) {
        return null;
      }

      if (renderOption) {
        return renderOption(itemProps, option, state);
      }

      const { key, ...rest } = itemProps;

      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <li key={key} {...rest}>
          {option.label}
        </li>
      );
    },
    [optionById, renderOption],
  );

  const resolvedFilterOptions = useMemo(() => {
    if (!filterOptions) {
      return undefined;
    }

    return (idsToFilter: TId[], state: Readonly<FilterOptionsState>) =>
      filterOptions(
        idsToFilter
          .map((id) => optionById.get(id))
          .filter((option): option is TOption => !!option),
        state,
      ).map((option) => option.id);
  }, [filterOptions, optionById]);

  return (
    <Autocomplete<TId>
      disabled={disabled}
      filterOptions={resolvedFilterOptions}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionLabel}
      groupBy={getOptionGroup}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      onChange={handleChange}
      onClose={onClose}
      onOpen={onOpen}
      open={open}
      options={ids}
      renderInput={renderInput}
      renderOption={resolvedRenderOption}
      value={autocompleteValue}
    />
  );
};
